import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Card, Button } from 'reactstrap';
import AdminHeader from '../../../components/header/AdminHeader';
import FormSelect from '../../../components/form/FormSelect';
import { refreshTimeOptions } from '../../../utils/common';
import AiReportCount from './AiReportCount';
import AiReportList from './AiReportList';
import AiTranscriptModal from './AiTranscriptModal';
import ProcessDetails from './ProcessDetails';
import ReportLiveData from './ReportLiveData';
import ClearBufferConfirmationModal from './ClearBufferConfirmationModal';
import { toast } from 'react-toastify';
import {
  aiProcessListGet,
  aiCountGet,
  aiListGet,
  aiTranscriptGet,
  aiLiveDataGet,
  aiClearBuffer,
  clearAiTranscript,
  useAiProcesses,
  useAiDashboard,
  useAiActiveCalls,
  useAiLiveData,
  useAiTranscript
} from '../../../store/slices/tenantSlices/aiRealtimeReportSlice';

const AiRealtimeReport = () => {
  const dispatch = useDispatch();
  const processes = useAiProcesses();
  const dashboardData = useAiDashboard();
  const activeCalls = useAiActiveCalls();
  const transcriptData = useAiTranscript();
  const liveData = useAiLiveData();

  const [refreshTime, setRefreshTime] = useState(5);
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [bufferClearModalOpen, setBufferClearModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  const fetchProcessList = async () => {
    try {
      await dispatch(aiProcessListGet()).unwrap();
    } catch (error) {
      console.error('Error fetching process list:', error);
    }
  };

  const fetchData = async () => {
    try {
      const payload = {};
      await dispatch(aiCountGet(payload)).unwrap();
      await dispatch(aiListGet(payload)).unwrap();
      await dispatch(aiLiveDataGet()).unwrap();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  useEffect(() => {
    fetchProcessList();
  }, []);

  useEffect(() => {
    fetchData();
    if (refreshTime) {
      const intervalId = setInterval(fetchData, refreshTime * 1000);
      return () => clearInterval(intervalId);
    }
  }, [refreshTime]);

  const handleTranscriptClick = (call) => {
    setSelectedCall(call);
    setTranscriptModalOpen(true);
    fetchTranscript(call.id);
  };

  const fetchTranscript = async (id) => {
    setTranscriptLoading(true);
    try {
      await dispatch(aiTranscriptGet(id)).unwrap();
    } catch (error) {
      console.error('Failed to fetch transcript:', error);
    } finally {
      setTranscriptLoading(false);
    }
  };

  const handleTranscriptClose = () => {
    setTranscriptModalOpen(false);
    setSelectedCall(null);
    dispatch(clearAiTranscript());
  };

  const handleTranscriptRefresh = () => {
    if (selectedCall) {
      fetchTranscript(selectedCall.id);
    }
  };

  const onBufferClear = async () => {
    try {
      const response = await dispatch(aiClearBuffer()).unwrap();
      toast.success(response?.message);
      setBufferClearModalOpen(false);
    } catch (error) {
      console.error('Error clearing buffer:', error);
    }
  };

  return (
    <Fragment>
      <AdminHeader title="AI Realtime Report" />
      <Row>
        <Col lg="2" md="2" className="px-2">
          <Card className="m-0 ai-process-details">
            <ProcessDetails data={processes} isClickable={false} />
          </Card>
        </Col>

        <Col lg="8" md="8" className="px-2">
          <AiReportCount data={dashboardData} />
          <Card className="m-0">
            <AiReportList data={activeCalls} onTranscriptClick={handleTranscriptClick} />
          </Card>
        </Col>

        <Col lg="2" md="2" className="px-2">
          <Card className="mb-3 px-2 py-2">
            <Row>
              <Col lg="12" md="12" className="mb-2">
                <FormSelect
                  isShowLabel={false}
                  name="refreshTime"
                  value={refreshTime}
                  placeholder="Select Refresh"
                  options={refreshTimeOptions}
                  onChange={(e) => setRefreshTime(Number(e.target.value))}
                />
              </Col>
              <Col lg="12" md="12">
                <Button
                  color="primary"
                  className="px-4 w-100"
                  type="submit"
                  onClick={() => setBufferClearModalOpen(true)}>
                  Clear Buffer
                </Button>
              </Col>
            </Row>
          </Card>
          <div className="live-data-container ai-live-data-container">
            <Card className="m-0 mb-3">
              <ReportLiveData data={liveData?.ringing_data} title="Ringing" />
            </Card>
            <Card className="m-0">
              <ReportLiveData data={liveData?.waiting_data} title="Waiting" />
            </Card>
          </div>
        </Col>
      </Row>

      <AiTranscriptModal
        isOpen={transcriptModalOpen}
        onClose={handleTranscriptClose}
        data={transcriptData}
        isLoading={transcriptLoading}
        onRefresh={handleTranscriptRefresh}
      />

      <ClearBufferConfirmationModal
        isOpen={bufferClearModalOpen}
        onClearClick={() => onBufferClear()}
        onCloseClick={() => setBufferClearModalOpen(false)}
      />
    </Fragment>
  );
};

export default AiRealtimeReport;
